import React, { useState, useEffect } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport, Inject } from '@syncfusion/ej2-react-grids';
import { Header } from '../Components';
import axios from 'axios';
import { FaSearch, FaTrash, FaFileExcel, FaFilePdf, FaEye, FaTimes, FaUserPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useBaseUrl } from '../Contexts/BaseUrlContext';

// Grid column configuration
const userGrid = [
  { 
    field: 'id', 
    headerText: 'ID', 
    width: 80, 
    textAlign: 'Center', 
    headerTemplate: () => <span className="font-bold">ID</span>,
    template: (props) => (
      <div className="text-center cursor-pointer text-blue-500 hover:underline" onClick={() => props.viewHandler(props.id)}>
        {props.id}
      </div>
    )
  },
  { 
    field: 'name', 
    headerText: 'Name', 
    width: 150, 
    textAlign: 'Center', 
    headerTemplate: () => <span className="font-bold">Name</span>,
    template: (props) => (
      <div className="text-center cursor-pointer text-blue-500 hover:underline" onClick={() => props.viewHandler(props.id)}>
        {props.name}
      </div>
    )
  },
  { 
    field: 'email_id', 
    headerText: 'Email', 
    width: 200, 
    textAlign: 'Center', 
    headerTemplate: () => <span className="font-bold">Email</span>,
  },
  { 
    field: 'role', 
    headerText: 'Role', 
    width: 120, 
    textAlign: 'Center', 
    headerTemplate: () => <span className="font-bold">Role</span>,
  },
  { 
    field: 'date_time', 
    headerText: 'Created At', 
    width: 150, 
    textAlign: 'Center', 
    headerTemplate: () => <span className="font-bold">Created At</span>,
  },
  {
    field: 'Actions',
    headerText: 'Actions',
    width: 120,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Actions</span>,
    template: (props) => (
      <div className="flex justify-center gap-3">
        <button
          className="text-blue-600 hover:text-blue-800 transition-transform transform hover:scale-110 focus:outline-none"
          onClick={() => props.viewHandler(props.id)}
          title="View Details"
        >
          <FaEye size={18} />
        </button>
        <button
          className="text-red-600 hover:text-red-800 transition-transform transform hover:scale-110 focus:outline-none"
          onClick={() => props.deleteHandler(props.id)}
          title="Delete"
        >
          <FaTrash size={18} />
        </button>
      </div>
    ),
  },
];

const UserList = () => {
  const [userData, setUserData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [updatedUser, setUpdatedUser] = useState({ name: '', role: '' });
  const [newUser, setNewUser] = useState({ name: '', email_id: '', password: '', role: 'user' });
  const [showAddUser, setShowAddUser] = useState(false);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  

  // Fetch user list from API
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setErrorMessage('');
      const token = localStorage.getItem('authToken');
      if (!token) {
        setErrorMessage('Please log in to access this page.');
        navigate('/');
        setIsLoading(false);
        return;
      }

      try {
        const formData = new FormData();
        formData.append('op', 'readAdmin');

        const response = await axios.post(
          `${baseUrl}/admin.php`,
          formData,
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
        console.log('User List Response:', response.data);
        setUserData(response.data || []);
      } catch (error) {
        console.error('Axios Error:', error.response || error);
        setErrorMessage(error.response?.data?.error || 'Error fetching user list.');
        if (error.response?.status === 401) {
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, [navigate]);

  // Fetch user by ID
  const viewHandler = async (id) => {
    setIsLoading(true);
    setErrorMessage('');
    const token = localStorage.getItem('authToken');
    if (!token) {
      setErrorMessage('Please log in to access this page.');
      navigate('/');
      setIsLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('op', 'readAdmin');
      formData.append('id', id);

      const response = await axios.post(
        `${baseUrl}/admin.php`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );
      console.log('User Details Response:', response.data);
      if (response.data && response.data.id) {
        setSelectedUser(response.data);
        setUpdatedUser({ name: response.data.name, role: response.data.role });
      } else {
        setErrorMessage('Failed to fetch user details.');
      }
    } catch (error) {
      console.error('Error fetching user:', error.response || error);
      setErrorMessage(error.response?.data?.error || 'Error fetching user details.');
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle add user
  const addUserHandler = async () => {
    if (!newUser.name || !newUser.email_id || !newUser.password) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    const token = localStorage.getItem('authToken');
    if (!token) {
      setErrorMessage('Please log in to access this page.');
      navigate('/');
      setIsLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('op', 'addAdmin');
      formData.append('name', newUser.name);
      formData.append('email_id', newUser.email_id);
      formData.append('password', newUser.password);
      formData.append('role', newUser.role);

      const response = await axios.post(
        `${baseUrl}/admin.php`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );
      console.log('Add User Response:', response.data);
      if (response.data.success) {
        setUserData([...userData, { 
          id: response.data.id, 
          name: newUser.name, 
          email_id: newUser.email_id, 
          role: newUser.role, 
          date_time: new Date().toISOString().slice(0, 19).replace('T', ' ')
        }]);
        alert('User added successfully.');
        setNewUser({ name: '', email_id: '', password: '', role: 'user' });
        setShowAddUser(false);
      } else {
        setErrorMessage('Failed to add user.');
      }
    } catch (error) {
      console.error('Error adding user:', error.response || error);
      setErrorMessage(error.response?.data?.error || 'Error adding user.');
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle update user
  const updateUserHandler = async () => {
    if (!updatedUser.name || !updatedUser.role) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    const token = localStorage.getItem('authToken');
    if (!token) {
      setErrorMessage('Please log in to access this page.');
      navigate('/');
      setIsLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('op', 'updateAdmin');
      formData.append('id', selectedUser.id);
      formData.append('name', updatedUser.name);
      formData.append('role', updatedUser.role);

      const response = await axios.post(
        `${baseUrl}/admin.php`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );
      console.log('Update User Response:', response.data);
      if (response.data.success) {
        setUserData(userData.map((user) =>
          user.id === selectedUser.id ? { ...user, name: updatedUser.name, role: updatedUser.role } : user
        ));
        alert('User updated successfully.');
        setSelectedUser(null);
        setUpdatedUser({ name: '', role: '' });
      } else {
        setErrorMessage('Failed to update user.');
      }
    } catch (error) {
      console.error('Error updating user:', error.response || error);
      setErrorMessage(error.response?.data?.error || 'Error updating user.');
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete user
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setIsLoading(true);
      setErrorMessage('');
      const token = localStorage.getItem('authToken');
      if (!token) {
        setErrorMessage('Please log in to access this page.');
        navigate('/');
        setIsLoading(false);
        return;
      }

      try {
        const formData = new FormData();
        formData.append('op', 'deleteAdmin');
        formData.append('id', id);

        const response = await axios.post(
          `${baseUrl}/admin.php`,
          formData,
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
        console.log('Delete Response:', response.data);
        if (response.data.success) {
          setUserData(userData.filter((item) => item.id !== id));
          alert('User deleted successfully.');
        } else {
          setErrorMessage('Failed to delete user.');
        }
      } catch (error) {
        console.error('Error deleting user:', error.response || error);
        setErrorMessage(error.response?.data?.error || 'Failed to delete user.');
        if (error.response?.status === 401) {
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Search filter
  const filteredData = userData.filter((item) =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.id.toString().includes(searchTerm)
  );

  // Inject handlers into grid data
  const gridData = filteredData.map((item) => ({
    ...item,
    viewHandler,
    deleteHandler,
  }));

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fadeIn bg-gray-100 min-h-screen">
      <Header category="Dashboard" title="User List" />

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          {errorMessage}
        </div>
      )}

      {/* Search and Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or ID..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 bg-white shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => setShowAddUser(true)}
            disabled={isLoading}
          >
            <FaUserPlus className="mr-2" /> Add User
          </button>
          <button
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => document.getElementById('userGrid').ej2_instances[0].excelExport()}
            disabled={isLoading}
          >
            <FaFileExcel className="mr-2" /> Export Excel
          </button>
          <button
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => document.getElementById('userGrid').ej2_instances[0].pdfExport()}
            disabled={isLoading}
          >
            <FaFilePdf className="mr-2" /> Export PDF
          </button>
        </div>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Grid */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <GridComponent
          id="userGrid"
          dataSource={gridData}
          allowPaging={true}
          allowSorting={true}
          allowExcelExport={true}
          allowPdfExport={true}
          allowFiltering={true}
          allowResizing={true}
          pageSettings={{ pageSize: 10, pageSizes: [10, 20, 50] }}
          filterSettings={{ type: 'Menu', showFilterBarStatus: true }}
        >
          <ColumnsDirective>
            {userGrid.map((item, index) => (
              <ColumnDirective key={index} {...item} />
            ))}
          </ColumnsDirective>
          <Inject services={[Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport]} />
        </GridComponent>
      </div>

      {/* Modal for Adding User */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Add New User</h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition-colors"
                onClick={() => setShowAddUser(false)}
              >
                <FaTimes size={24} />
              </button>
            </div>
            <div className="space-y-3 text-gray-700">
              <input
                type="text"
                placeholder="Name"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
              <input
                type="email"
                placeholder="Email"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newUser.email_id}
                onChange={(e) => setNewUser({ ...newUser, email_id: e.target.value })}
              />
              <input
                type="password"
                placeholder="Password"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
              <select
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                onClick={() => setShowAddUser(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                onClick={addUserHandler}
                disabled={isLoading || !newUser.name || !newUser.email_id || !newUser.password}
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Viewing and Updating User Details */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 w-full max-w-md h-[80vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-gray-900">User Details</h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition-colors"
                onClick={() => {
                  setSelectedUser(null);
                  setUpdatedUser({ name: '', role: '' });
                }}
              >
                <FaTimes size={24} />
              </button>
            </div>
            <div className="space-y-3 text-gray-700">
              <p><strong>ID:</strong> {selectedUser.id}</p>
              <p><strong>Email:</strong> {selectedUser.email_id}</p>
              <p><strong>Created At:</strong> {selectedUser.date_time}</p>
              <input
                type="text"
                placeholder="Update Name"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={updatedUser.name}
                onChange={(e) => setUpdatedUser({ ...updatedUser, name: e.target.value })}
              />
              <select
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={updatedUser.role}
                onChange={(e) => setUpdatedUser({ ...updatedUser, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="mt-6 flex justify-end gap-2 sticky bottom-0 bg-white pt-4">
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                onClick={() => {
                  setSelectedUser(null);
                  setUpdatedUser({ name: '', role: '' });
                }}
              >
                Close
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                onClick={updateUserHandler}
                disabled={isLoading || !updatedUser.name || !updatedUser.role}
              >
                Update User
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                onClick={() => {
                  deleteHandler(selectedUser.id);
                  setSelectedUser(null);
                  setUpdatedUser({ name: '', role: '' });
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;